import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { RootState } from '../store';
import {
  useGetInvitesQuery,
  useCreateInviteMutation,
  useRevokeInviteMutation,
  useResendInviteMutation,
  Invite,
} from '../store/adminApi';
import DataTable from './DataTable';
import Pagination from './Pagination';
import ConfirmModal from './ConfirmModal';

const ITEMS_PER_PAGE = 10;

const statusBadge = (status: Invite['status']) => {
  switch (status) {
    case 'pending': return <span className="badge-yellow">⏳ Pending</span>;
    case 'accepted': return <span className="badge-green">✓ Accepted</span>;
    case 'revoked': return <span className="badge-gray">Revoked</span>;
    case 'expired': return <span className="badge-red">Expired</span>;
  }
};

const Invites: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { data: invites = [], isLoading, refetch } = useGetInvitesQuery();
  const [createInvite, { isLoading: creating }] = useCreateInviteMutation();
  const [revokeInvite] = useRevokeInviteMutation();
  const [resendInvite] = useResendInviteMutation();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<{ email: string; name: string; designation: string; role: 'admin' | 'user' }>({
    email: '', name: '', designation: '', role: 'user',
  });
  const [formErr, setFormErr] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [targetInvite, setTargetInvite] = useState<Invite | null>(null);

  const canInviteAdmin = currentUser?.role === 'superadmin';

  const totalPages = Math.ceil(invites.length / ITEMS_PER_PAGE);
  const paginated = invites.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const validate = () => {
    const err: Record<string, string> = {};
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) err.email = 'Valid email required';
    setFormErr(err);
    return Object.keys(err).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    try {
      await createInvite(form).unwrap();
      toast.success(`Invitation sent to ${form.email}`);
      setForm({ email: '', name: '', designation: '', role: 'user' });
      setShowForm(false);
      refetch();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to send invite');
    }
  };

  const handleRevoke = async (invite: Invite) => {
    try {
      await revokeInvite(invite._id).unwrap();
      toast.success('Invite revoked');
      setConfirmOpen(false);
      refetch();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to revoke');
    }
  };

  const handleResend = async (invite: Invite) => {
    try {
      await resendInvite(invite._id).unwrap();
      toast.success(`Invitation resent to ${invite.email}`);
      refetch();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to resend');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display font-bold text-2xl text-trident-gray-900">Invitations</h2>
          <p className="text-sm text-trident-gray-500">
            Invite new users by email. They receive a one-click signup link.
          </p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Send Invite
        </button>
      </div>

      {showForm && (
        <div className="card mb-5 border-2 border-trident-orange/30 animate-slide-up">
          <h3 className="font-display font-bold text-lg text-trident-gray-900 mb-4">New Invitation</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-display font-semibold text-trident-gray-600 uppercase tracking-wider mb-1.5">Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-field"
                placeholder="colleague@example.com"
              />
              {formErr.email && <p className="text-xs text-red-500 mt-1">{formErr.email}</p>}
            </div>
            <div>
              <label className="block text-xs font-display font-semibold text-trident-gray-600 uppercase tracking-wider mb-1.5">Full Name (optional)</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-field"
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <label className="block text-xs font-display font-semibold text-trident-gray-600 uppercase tracking-wider mb-1.5">Designation (optional)</label>
              <input
                type="text"
                value={form.designation}
                onChange={(e) => setForm({ ...form, designation: e.target.value })}
                className="input-field"
                placeholder="QA Engineer"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-display font-semibold text-trident-gray-600 uppercase tracking-wider mb-1.5">Role</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, role: 'user' })}
                  className={`flex-1 p-3 rounded-xl border-2 text-left transition ${
                    form.role === 'user'
                      ? 'border-trident-orange bg-trident-orange-light'
                      : 'border-trident-gray-200 hover:border-trident-gray-300'
                  }`}
                >
                  <div className="font-display font-bold text-sm text-trident-gray-900">User</div>
                  <div className="text-xs text-trident-gray-500">Can fill assigned forms</div>
                </button>
                {canInviteAdmin && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, role: 'admin' })}
                    className={`flex-1 p-3 rounded-xl border-2 text-left transition ${
                      form.role === 'admin'
                        ? 'border-trident-orange bg-trident-orange-light'
                        : 'border-trident-gray-200 hover:border-trident-gray-300'
                    }`}
                  >
                    <div className="font-display font-bold text-sm text-trident-gray-900">Admin</div>
                    <div className="text-xs text-trident-gray-500">Can manage sprints & users</div>
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-5">
            <button onClick={handleCreate} disabled={creating} className="btn-primary">
              {creating ? 'Sending...' : 'Send Invitation'}
            </button>
            <button onClick={() => { setShowForm(false); setFormErr({}); }} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      <DataTable
        loading={isLoading}
        rows={paginated}
        rowKey={(r) => r._id}
        emptyMessage="No invites sent yet"
        columns={[
          {
            key: 'email', label: 'Invitee',
            render: (inv: Invite) => (
              <div>
                <div className="font-semibold text-trident-gray-900">{inv.name || inv.email.split('@')[0]}</div>
                <div className="text-xs text-trident-gray-500">{inv.email}</div>
              </div>
            ),
          },
          {
            key: 'role', label: 'Role',
            render: (inv: Invite) => inv.role === 'admin'
              ? <span className="badge-orange">Admin</span>
              : <span className="badge-blue">User</span>,
          },
          {
            key: 'status', label: 'Status',
            render: (inv: Invite) => statusBadge(inv.status),
          },
          {
            key: 'invitedBy', label: 'Invited by',
            render: (inv: Invite) => (
              <span className="text-sm text-trident-gray-600">
                {inv.invitedBy?.name || '—'}
              </span>
            ),
          },
          {
            key: 'expiresAt', label: 'Expires',
            render: (inv: Invite) => (
              <span className="text-xs text-trident-gray-500">
                {new Date(inv.expiresAt).toLocaleDateString()}
              </span>
            ),
          },
          {
            key: 'actions', label: 'Actions', align: 'right',
            render: (inv: Invite) => (
              <div className="flex items-center justify-end gap-2">
                {inv.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleResend(inv)}
                      className="text-xs font-semibold text-trident-orange hover:underline"
                    >
                      Resend
                    </button>
                    <button
                      onClick={() => { setTargetInvite(inv); setConfirmOpen(true); }}
                      className="text-xs font-semibold text-red-500 hover:underline"
                    >
                      Revoke
                    </button>
                  </>
                )}
                {(inv.status === 'expired' || inv.status === 'revoked') && (
                  <button
                    onClick={() => handleResend(inv)}
                    className="text-xs font-semibold text-trident-orange hover:underline"
                  >
                    Re-send
                  </button>
                )}
              </div>
            ),
          },
        ]}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={invites.length}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={setCurrentPage}
      />

      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => targetInvite && handleRevoke(targetInvite)}
        title="Revoke invitation?"
        message={`Revoke the invitation for ${targetInvite?.email}? The invite link will no longer work.`}
        confirmText="Revoke"
        confirmButtonColor="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
};

export default Invites;
